import { DtFilterField, DtFilterValue } from '../helpers';
import { DateRangeValue, getUiOptionLabel, UiOption, getNiceDate } from '@/lib/helpers';

export interface FilterHeaderColProps {
  values: DtFilterValue[];
  setValueForColumn: (columnName: string, value: unknown) => void;
  setColumnToDefault: (columnName: string) => void;
}

export interface SingleFilterHeaderFieldControlProps extends FilterHeaderColProps {
  field: DtFilterField;
}

export const isAllDates = (value: unknown): boolean => {
  if (!Array.isArray(value) || value.length < 2) {
    return true;
  }
  const [start, end] = value as DateRangeValue;
  return start == null || end == null;
};

export const getFieldValue = (
  values: DtFilterValue[],
  columnName: string
): DtFilterValue['value'] => {
  const entry = values.find((v) => v.columnName === columnName);
  return entry?.value;
};

export const getSelectedOptionValues = (
  current: string[] | undefined,
  options: UiOption[]
): string[] => {
  if (!current?.length) {
    return [];
  }
  return options.map(({ value }) => value).filter((value) => current.includes(value));
};

export const hasFieldValues = (
  control: DtFilterField['control'],
  currentValue: DtFilterValue['value'],
  options: UiOption[]
): boolean => {
  switch (control) {
    case 'select-multi': {
      const currentValues = Array.isArray(currentValue) ? (currentValue as string[]) : [];
      return !!getSelectedOptionValues(currentValues, options).length;
    }
    case 'text-multi':
      return Array.isArray(currentValue) && !!currentValue.length;
    case 'select-search':
    case 'select':
      return !!(currentValue as string)?.length;
    case 'date-range':
    case 'text':
    default:
      return ((currentValue as string) ?? '') !== '';
  }
};

type ChipSummary = {
  value: string;
  extraCount?: number;
};

const summarizeLabels = (labels: string[], maxVisibleChips = 2): ChipSummary => {
  if (!labels.length) {
    return { value: 'All' };
  }
  if (labels.length <= maxVisibleChips) {
    return { value: labels.join(', ') };
  }
  const visible = labels.slice(0, maxVisibleChips);
  const rest = labels.length - maxVisibleChips;
  return { value: visible.join(', '), extraCount: rest };
};

/**
 * Returns display text for the filter header chip, showing actual selected value(s).
 * For select-multi with more than 2 selected: "Item1, Item2 +N others".
 */
export const getChipSummary = (
  control: DtFilterField['control'],
  currentValue: DtFilterValue['value'],
  options: UiOption[]
): ChipSummary => {
  switch (control) {
    case 'select-multi': {
      const currentValues = Array.isArray(currentValue) ? (currentValue as string[]) : [];
      const selectedValues = getSelectedOptionValues(currentValues, options);
      const valueToLabel = new Map(options.map((o) => [o.value, getUiOptionLabel(o)]));
      const labels = selectedValues.map((v) => valueToLabel.get(v) ?? v).filter(Boolean);
      return summarizeLabels(labels);
    }
    case 'text-multi': {
      const labels = Array.isArray(currentValue) ? (currentValue as string[]) : [];
      return summarizeLabels(labels);
    }
    case 'select': {
      const singleVal = (currentValue as string) ?? '';
      if (singleVal === '') {
        return { value: 'All' };
      }
      const opt = options.find((o) => o.value === singleVal);
      return { value: opt ? getUiOptionLabel(opt) : singleVal };
    }
    case 'select-search': {
      if (currentValue == null) {
        return { value: 'All' };
      }

      const selectedOptions = Array.isArray(currentValue)
        ? (currentValue as UiOption[])
        : [currentValue as UiOption];

      const labels = selectedOptions.map((option) => getUiOptionLabel(option)).filter(Boolean);
      return summarizeLabels(labels);
    }
    case 'date-range': {
      if (isAllDates(currentValue)) {
        return { value: 'Last 30 days' };
      }
      const [start, end] = currentValue as DateRangeValue;
      if (start && end) {
        return { value: `${getNiceDate(start).date} – ${getNiceDate(end).date}` };
      }
      if (start) {
        return { value: getNiceDate(start).date };
      }
      if (end) {
        return { value: getNiceDate(end).date };
      }
      return { value: 'Last 30 days' };
    }
    case 'text':
    default: {
      const textVal = (currentValue as string) ?? '';
      return { value: textVal === '' ? 'All' : textVal };
    }
  }
};
